import { useEffect, useState } from 'react';
import { Button, InlineNotification, Layer, TextArea } from '@carbon/react';
import { Close } from '@carbon/react/icons';

import {
  parseTaskError,
  useSaveInventoryWorkspaceTask,
} from './use-inventory-workspace-tasks';
// The Type Curve decision panel's stylesheet, reused wholesale — the layout is
// identical, only the wiring differs (inventory gate task vs the TC overall task).
import style from 'components/type-curve-workspace/tc-verification-decision-panel.module.scss';

// The workspace-level gate task the header verdict patches.
const GATE_TASK_PATH = 'inventory_workspace';

interface InventoryVerificationPanelProps {
  workspaceId: number;
  /** The verdict being set — fixed by which header button opened the panel. */
  kind: 'correct' | 'incorrect';
  onClose: () => void;
}

/**
 * The workspace-gate verdict decision panel. Opened from the header's Mark as
 * Correct / Incorrect buttons; captures a required reason and, on Save, patches
 * the `inventory_workspace` gate task, then closes. The verdict is fixed by
 * `kind` (no in-panel toggle) — mirrors the Type Curve workspace's decision
 * panel.
 *
 * @param {InventoryVerificationPanelProps} props - Workspace id, verdict kind, close handler.
 * @return {JSX.Element} The decision panel.
 */
export const InventoryVerificationPanel = ({
  workspaceId,
  kind,
  onClose,
}: InventoryVerificationPanelProps) => {
  const [reason, setReason] = useState('');
  const [reasonBlurred, setReasonBlurred] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const save = useSaveInventoryWorkspaceTask(workspaceId);

  // Re-opening with the other verdict starts clean.
  useEffect(() => {
    setReason('');
    setReasonBlurred(false);
    setSaveError(null);
  }, [kind]);

  const isCorrect = kind === 'correct';
  const title = isCorrect ? 'Mark as Correct' : 'Mark as Incorrect';
  const helperSubtitle = isCorrect
    ? 'Why are you marking this inventory workspace as correct?'
    : 'Why are you marking this inventory workspace as incorrect?';
  const placeholder = isCorrect
    ? 'Explain why this inventory workspace is correct.'
    : 'Explain why this inventory workspace is incorrect.';

  const reasonTrimmed = reason.trim();
  const reasonInvalid = reasonBlurred && reasonTrimmed.length === 0;
  const canSubmit = reasonTrimmed.length > 0 && !save.isPending;

  const handleSave = () => {
    if (!reasonTrimmed) {
      setReasonBlurred(true);
      return;
    }
    setSaveError(null);
    save.mutate(
      {
        path: GATE_TASK_PATH,
        correctnessState: isCorrect,
        reason: reasonTrimmed,
      },
      {
        onSuccess: () => onClose(),
        onError: async (error) => {
          const message = await parseTaskError(error);
          setSaveError(message ?? 'Unable to save. Please try again.');
        },
      }
    );
  };

  return (
    <div className={style.root}>
      <div className={style.panelHeader}>
        <div className={style.headerCopy}>
          <h2 className={style.title}>{title}</h2>
          <p className={style.subtitle}>{helperSubtitle}</p>
        </div>
        <Button
          hasIconOnly
          size="lg"
          kind="ghost"
          onClick={onClose}
          iconDescription="Close"
          tooltipAlignment="center"
          tooltipPosition="bottom"
          renderIcon={(props) => <Close size={20} {...props} />}
        />
      </div>
      <div className={style.panelBody}>
        {saveError ? (
          <InlineNotification
            kind="error"
            title="Save failed"
            subtitle={saveError}
            lowContrast
            hideCloseButton
            className={style.errorNote}
          />
        ) : null}
        <div className={style.fieldWrap}>
          <Layer>
            <TextArea
              id="inventory-verification-reason"
              labelText="Reason"
              placeholder={placeholder}
              value={reason}
              invalid={reasonInvalid}
              invalidText={reasonInvalid ? 'A reason is required.' : undefined}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setReasonBlurred(true)}
              className={style.field}
              disabled={save.isPending}
            />
          </Layer>
        </div>
      </div>
      <div className={style.footer}>
        <Button
          kind="secondary"
          size="lg"
          onClick={onClose}
          disabled={save.isPending}
        >
          Cancel
        </Button>
        <Button
          kind="primary"
          size="lg"
          onClick={handleSave}
          disabled={!canSubmit}
        >
          Save
        </Button>
      </div>
    </div>
  );
};
